import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../lib/i18n';
import { api } from '../lib/auth';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, 
  TrendingUp, 
  Users, 
  MapPin, 
  Calendar,
  Euro,
  Eye,
  RefreshCw,
  Filter,
  BarChart3,
  FileText,
  Trash2,
  Boxes,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  X,
  CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Brush
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const YEAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('nl-BE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value);
};

// Compact currency format for large amounts (e.g., €951.340,83 -> €951,3K or €951.340)
const formatCurrencyCompact = (value) => {
  if (value === null || value === undefined) return '€0,00';
  
  const absValue = Math.abs(value);
  
  // For amounts over 1 million, use M suffix
  if (absValue >= 1000000) {
    const formatted = (value / 1000000).toFixed(2);
    return `€${formatted.replace('.', ',')}M`;
  }
  
  // For amounts over 100K, show without decimals for cleaner look
  if (absValue >= 100000) {
    return new Intl.NumberFormat('nl-BE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  // For normal amounts, show full format
  return new Intl.NumberFormat('nl-BE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value);
};

const SHORT_MONTHS = {
  tr: ['', 'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
  en: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  nl: ['', 'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
  fr: ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
};
const TR_SHORT = SHORT_MONTHS.tr;

const translateMonth = (turkishName) => {
  const lang = localStorage.getItem('oms-language') || 'nl';
  if (lang === 'tr') return turkishName;
  const idx = TR_SHORT.indexOf(turkishName);
  if (idx > 0) return (SHORT_MONTHS[lang] || SHORT_MONTHS.nl)[idx];
  return turkishName;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('nl-BE').format(value);
};

export default function Packages() {
  const { t, language: currentLang } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [yearlyComparison, setYearlyComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteInvoice, setDeleteInvoice] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  
  // Chart view mode states
  const [weeklyTrendMode, setWeeklyTrendMode] = useState('amount'); // 'amount' or 'count'
  const [visibleYears, setVisibleYears] = useState({}); // { 2024: true, 2025: true, 2026: true }
  const [yearlyChartMode, setYearlyChartMode] = useState('amount'); // 'amount' or 'count'
  const [weeklyComparisonMode, setWeeklyComparisonMode] = useState('count'); // 'amount' or 'count' - Haftalık Karşılaştırma (default: paket)
  const [topEmployeesMode, setTopEmployeesMode] = useState('count'); // 'amount' or 'count' - En Yüksek Kazançlı (default: paket)
  const [comparisonLocationFilter, setComparisonLocationFilter] = useState(''); // Location filter for Haftalık Karşılaştırma
  const [availableLocations, setAvailableLocations] = useState([]); // Available locations for comparison
  
  // New feature states - Driver-Location Matrix & Seasonal Trends
  const [driverLocationMatrix, setDriverLocationMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixMetric, setMatrixMetric] = useState('quantity'); // 'quantity' or 'amount'
  const [matrixTimePeriod, setMatrixTimePeriod] = useState('all'); // 'daily', 'weekly', 'monthly', 'all'
  const [seasonalTrends, setSeasonalTrends] = useState(null);
  const [seasonalLoading, setSeasonalLoading] = useState(false);
  const [seasonalMetric, setSeasonalMetric] = useState('quantity'); // 'quantity' or 'amount'
  const [seasonalViewMode, setSeasonalViewMode] = useState('monthly'); // 'monthly' or 'quarterly'
  const [seasonalComparisonMode, setSeasonalComparisonMode] = useState('yearly'); // 'yearly', 'last6months', 'last12months'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalInvoices, setTotalInvoices] = useState(0);
  
  // Available years from API
  const [availableYearsFromApi, setAvailableYearsFromApi] = useState([]);
  
  // Modal filters
  const [modalEmployeeSearch, setModalEmployeeSearch] = useState('');
  const [modalRegionFilter, setModalRegionFilter] = useState('');
  const [modalDateFilter, setModalDateFilter] = useState('');
  
  // Bonus calculation modal
  const [bonusModal, setBonusModal] = useState({ open: false, emp: null, fark: 0, days: 0 });
  const [birimFiyat, setBirimFiyat] = useState(0.5);
  const [birimFiyatInput, setBirimFiyatInput] = useState('0,5');
  const HEDEF_ORTALAMA = 165;
  
  // Filters
  const [monthFilter, setMonthFilter] = useState([]); // Multiple months selection
  const [weekFilter, setWeekFilter] = useState([]); // Multiple weeks selection
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString()); // Default to current year
  const [companyFilter, setCompanyFilter] = useState('');
  const [globalCompanyFilter, setGlobalCompanyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState(''); // Search by invoice number
  const [sortBy, setSortBy] = useState('invoice_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Date range filter
  const [useDateRange, setUseDateRange] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/invoices/companies');
      setAllCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Fetch locations for comparison filter
  const fetchLocations = async () => {
    try {
      const params = new URLSearchParams();
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      const response = await api.get(`/invoices/locations?${params.toString()}`);
      setAvailableLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchYearlyComparison = async () => {
    try {
      setComparisonLoading(true);
      const params = new URLSearchParams();
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      if (comparisonLocationFilter) params.append('location', comparisonLocationFilter);
      
      const response = await api.get(`/invoices/yearly-comparison?${params.toString()}`);
      setYearlyComparison(response.data);
    } catch (error) {
      console.error('Error fetching yearly comparison:', error);
    } finally {
      setComparisonLoading(false);
    }
  };

  // Fetch Driver-Location Matrix
  const fetchDriverLocationMatrix = async () => {
    try {
      setMatrixLoading(true);
      const params = new URLSearchParams();
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      if (yearFilter) params.append('year', yearFilter);
      if (weekFilter.length > 0) {
        weekFilter.forEach(week => params.append('week_numbers', week));
      }
      if (matrixTimePeriod && matrixTimePeriod !== 'all') {
        params.append('time_period', matrixTimePeriod);
      }
      
      const response = await api.get(`/invoices/driver-location-matrix?${params.toString()}`);
      setDriverLocationMatrix(response.data);
    } catch (error) {
      console.error('Error fetching driver-location matrix:', error);
      toast.error(t('packages.matrixError'));
    } finally {
      setMatrixLoading(false);
    }
  };

  // Fetch Seasonal Trends
  const fetchSeasonalTrends = async () => {
    try {
      setSeasonalLoading(true);
      const params = new URLSearchParams();
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      params.append('years', '3');
      if (seasonalComparisonMode && seasonalComparisonMode !== 'yearly') {
        params.append('comparison_mode', seasonalComparisonMode);
      }
      
      const response = await api.get(`/invoices/seasonal-trends?${params.toString()}`);
      setSeasonalTrends(response.data);
    } catch (error) {
      console.error('Error fetching seasonal trends:', error);
      toast.error(t('packages.seasonalError'));
    } finally {
      setSeasonalLoading(false);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const response = await api.get('/invoices/years');
      setAvailableYearsFromApi(response.data.years || []);
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Date range filter takes priority over week filter
      if (useDateRange && dateRangeStart && dateRangeEnd) {
        params.append('date_from', dateRangeStart);
        params.append('date_to', dateRangeEnd);
      } else {
        // Month filter
        if (monthFilter.length > 0) {
          monthFilter.forEach(m => params.append('months', m));
        }
        // Multiple weeks support
        if (weekFilter.length > 0) {
          weekFilter.forEach(week => params.append('week_number', week));
        }
        if (yearFilter) params.append('year', yearFilter);
      }
      
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      if (invoiceSearch) params.append('search', invoiceSearch);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('skip', ((currentPage - 1) * pageSize).toString());
      params.append('limit', pageSize.toString());
      
      const response = await api.get(`/invoices/list?${params.toString()}`);
      setInvoices(response.data.invoices || []);
      setTotalInvoices(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(t('packages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const params = new URLSearchParams();
      
      // Date range filter takes priority over week filter
      if (useDateRange && dateRangeStart && dateRangeEnd) {
        params.append('date_from', dateRangeStart);
        params.append('date_to', dateRangeEnd);
      } else {
        // Month filter
        if (monthFilter.length > 0) {
          monthFilter.forEach(m => params.append('months', m));
        }
        // Multiple weeks support
        if (weekFilter.length > 0) {
          weekFilter.forEach(week => params.append('week_number', week));
        }
        if (yearFilter) params.append('year', yearFilter);
      }
      
      if (globalCompanyFilter) params.append('company_name', globalCompanyFilter);
      
      const response = await api.get(`/invoices/statistics?${params.toString()}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error(t('packages.statsError'));
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchInvoiceDetail = async (invoiceNumber) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/invoices/detail/${invoiceNumber}`);
      setInvoiceDetail(response.data);
    } catch (error) {
      console.error('Error fetching invoice detail:', error);
      toast.error(t('packages.detailError'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteInvoice) return;
    
    try {
      setDeleting(true);
      await api.delete(`/invoices/delete/${deleteInvoice.invoice_number}`);
      toast.success(t('packages.deleteSuccess'));
      setDeleteInvoice(null);
      fetchInvoices();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error(t('packages.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    try {
      setDeleting(true);
      const invoicesToDelete = Array.from(selectedInvoices);
      let successCount = 0;
      let errorCount = 0;
      
      for (const invoiceNumber of invoicesToDelete) {
        try {
          await api.delete(`/invoices/delete/${invoiceNumber}`);
          successCount++;
        } catch (error) {
          console.error(`Error deleting invoice ${invoiceNumber}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} ${t('packages.invoicesDeleted')}`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} ${t('packages.invoicesDeleteError')}`);
      }
      
      setSelectedInvoices(new Set());
      setBulkDeleteDialog(false);
      fetchInvoices();
      fetchStatistics();
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast.error(t('packages.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const toggleInvoiceSelection = (invoiceNumber) => {
    setSelectedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(invoiceNumber)) {
        next.delete(invoiceNumber);
      } else {
        next.add(invoiceNumber);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.invoice_number)));
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStatistics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter, weekFilter, yearFilter, globalCompanyFilter, currentPage, pageSize, invoiceSearch, sortBy, sortOrder, useDateRange, dateRangeStart, dateRangeEnd]);

  // Fetch yearly comparison when company filter or location filter changes
  useEffect(() => {
    fetchYearlyComparison();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompanyFilter, comparisonLocationFilter]);

  // Fetch driver-location matrix when filters change
  useEffect(() => {
    fetchDriverLocationMatrix();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompanyFilter, yearFilter, monthFilter, weekFilter, matrixTimePeriod]);

  // Fetch seasonal trends when company filter or comparison mode changes
  useEffect(() => {
    fetchSeasonalTrends();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompanyFilter, seasonalComparisonMode]);

  // Fetch locations when company filter changes
  useEffect(() => {
    fetchLocations();
    setComparisonLocationFilter(''); // Reset location filter when company changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompanyFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [monthFilter, weekFilter, yearFilter, globalCompanyFilter, invoiceSearch, sortBy, sortOrder, useDateRange, dateRangeStart, dateRangeEnd]);

  // Fetch companies and years once on mount
  useEffect(() => {
    fetchCompanies();
    fetchAvailableYears();
    fetchLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setModalEmployeeSearch('');
    setModalRegionFilter('');
    setModalDateFilter('');
    fetchInvoiceDetail(invoice.invoice_number);
  };

  const handleCloseModal = () => {
    setSelectedInvoice(null);
    setModalEmployeeSearch('');
    setModalRegionFilter('');
    setModalDateFilter('');
  };

  const handleRefresh = () => {
    fetchInvoices();
    fetchStatistics();
    fetchCompanies(); // Refresh companies too
    toast.success(t('common.refreshed'));
  };

  // Prepare chart data
  const employeeChartData = useMemo(() => {
    if (!statistics?.employees) return [];
    const filteredData = statistics.employees
      .filter(emp => {
        const name = emp._id?.split(',')[0]?.toLowerCase() || '';
        return !name.includes('gast medewerker') && !name.includes('gast medew');
      })
      .map(emp => ({
        name: emp._id?.split(',')[0] || emp._id,
        earnings: emp.total_earnings || 0,
        quantity: emp.total_quantity || 0
      }));
    
    // Sort based on mode and take top 10
    return filteredData
      .sort((a, b) => topEmployeesMode === 'amount' 
        ? b.earnings - a.earnings 
        : b.quantity - a.quantity
      )
      .slice(0, 10);
  }, [statistics, topEmployeesMode]);

  const regionChartData = useMemo(() => {
    if (!statistics?.regions) return [];
    return statistics.regions.slice(0, 10).map(reg => ({
      name: reg.region || reg._id,
      value: reg.total_earnings || 0,
      employees: reg.employee_count || 0
    }));
  }, [statistics]);

  const weeklyChartData = useMemo(() => {
    if (!statistics?.weekly) return [];
    return statistics.weekly.map(w => ({
      week: `${t('packages.weekLabel')} ${w._id}`,
      amount: w.total_amount || 0,
      count: w.total_quantity || w.invoice_count || 0
    })).reverse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statistics]);

  // Filtered employees based on search
  const filteredEmployees = useMemo(() => {
    if (!statistics?.employees) return [];
    if (!employeeSearch.trim()) return statistics.employees;
    
    return statistics.employees.filter(emp => 
      turkishIncludes(emp._id || '', employeeSearch.trim())
    );
  }, [statistics, employeeSearch]);

  // Get unique companies and locations from invoices (for invoice tab filter)
  const uniqueCompanies = useMemo(() => {
    if (!invoices?.length) return [];
    return [...new Set(invoices.map(inv => inv.company_name).filter(Boolean))].sort();
  }, [invoices]);

  const uniqueLocations = useMemo(() => {
    if (!invoices?.length) return [];
    return [...new Set(invoices.map(inv => inv.location).filter(Boolean))].sort();
  }, [invoices]);

  // Filtered invoices based on company and location
  const filteredInvoices = useMemo(() => {
    if (!invoices?.length) return [];
    
    return invoices.filter(inv => {
      if (companyFilter && inv.company_name !== companyFilter) return false;
      if (locationFilter && inv.location !== locationFilter) return false;
      return true;
    });
  }, [invoices, companyFilter, locationFilter]);

  // Modal - unique regions and dates from invoice detail
  const modalUniqueRegions = useMemo(() => {
    if (!invoiceDetail?.employees?.length) return [];
    return [...new Set(invoiceDetail.employees.map(emp => emp.region).filter(Boolean))].sort();
  }, [invoiceDetail]);

  const modalUniqueDates = useMemo(() => {
    if (!invoiceDetail?.employees?.length) return [];
    return [...new Set(invoiceDetail.employees.map(emp => emp.work_date).filter(Boolean))].sort();
  }, [invoiceDetail]);

  // Modal - filtered employees
  const modalFilteredEmployees = useMemo(() => {
    if (!invoiceDetail?.employees?.length) return [];
    
    return invoiceDetail.employees.filter(emp => {
      // Employee name search
      if (modalEmployeeSearch) {
        if (!turkishIncludes(emp.employee_name || '', modalEmployeeSearch.trim())) return false;
      }
      // Region filter
      if (modalRegionFilter && emp.region !== modalRegionFilter) return false;
      // Date filter
      if (modalDateFilter && emp.work_date !== modalDateFilter) return false;
      return true;
    });
  }, [invoiceDetail, modalEmployeeSearch, modalRegionFilter, modalDateFilter]);

  // Modal - calculate totals for filtered employees
  const modalFilteredTotals = useMemo(() => {
    return {
      quantity: modalFilteredEmployees.reduce((sum, emp) => sum + (emp.quantity || 0), 0),
      total: modalFilteredEmployees.reduce((sum, emp) => sum + (emp.total || 0), 0)
    };
  }, [modalFilteredEmployees]);

  // Get available years from API or fallback
  const availableYears = useMemo(() => {
    if (availableYearsFromApi.length > 0) {
      return availableYearsFromApi;
    }
    // Fallback if API hasn't loaded yet
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  }, [availableYearsFromApi]);

  // Get available weeks (1-52)
  const availableWeeks = useMemo(() => {
    return Array.from({ length: 52 }, (_, i) => i + 1);
  }, []);

  return (
    <div className="space-y-6" data-testid="packages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Package className="h-6 w-6 text-blue-600" />
            {t('packages.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {t('packages.subtitle')}
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                {t('common.filters')}:
              </span>
            </div>
            <Select 
              value={yearFilter || "all"} 
              onValueChange={(v) => setYearFilter(v === "all" ? "" : v)}
              disabled={useDateRange}
            >
              <SelectTrigger className={`w-[120px] ${useDateRange ? 'opacity-50' : ''}`}>
                <SelectValue placeholder={t('packages.year')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('packages.year')} - {t('packages.allWeeks')}</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Month Filter - Multi Select */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`w-[160px] justify-between ${useDateRange ? 'opacity-50' : ''}`}
                  disabled={useDateRange}
                  data-testid="month-filter-trigger"
                >
                  <div className="flex items-center gap-1.5 truncate">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="truncate">
                      {monthFilter.length === 0 
                        ? (t('packages.selectMonth') || 'Ay Seçin')
                        : monthFilter.length === 1
                          ? (() => {
                              const monthNames = { tr: ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'], nl: ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'], en: ['January','February','March','April','May','June','July','August','September','October','November','December'], fr: ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'] };
                              return (monthNames[currentLang] || monthNames.nl)[parseInt(monthFilter[0]) - 1];
                            })()
                          : `${monthFilter.length} ${t('packages.monthsSelected') || 'ay'}`
                      }
                    </span>
                  </div>
                  {monthFilter.length > 0 && (
                    <span className="ml-1 bg-blue-100 text-blue-700 text-xs rounded-full px-1.5 py-0.5 shrink-0">
                      {monthFilter.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2" align="start">
                <div className="flex items-center justify-between mb-2 pb-2 border-b">
                  <span className="text-xs font-medium text-slate-500">{t('packages.selectMonth') || 'Ay Seçin'}</span>
                  {monthFilter.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400 hover:text-red-500" onClick={() => setMonthFilter([])}>
                      <X className="h-3 w-3 mr-1" />{t('packages.clearSelection') || 'Temizle'}
                    </Button>
                  )}
                </div>
                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                  {(() => {
                    const monthNames = { tr: ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'], nl: ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'], en: ['January','February','March','April','May','June','July','August','September','October','November','December'], fr: ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'] };
                    const names = monthNames[currentLang] || monthNames.nl;
                    return names.map((name, idx) => {
                      const val = (idx + 1).toString();
                      const isSelected = monthFilter.includes(val);
                      return (
                        <div
                          key={val}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                          onClick={() => {
                            if (isSelected) setMonthFilter(monthFilter.filter(m => m !== val));
                            else setMonthFilter([...monthFilter, val]);
                          }}
                          data-testid={`month-option-${val}`}
                        >
                          <Checkbox checked={isSelected} className="h-4 w-4" />
                          <span className="text-sm">{name}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </PopoverContent>
            </Popover>

            {/* Multiple Week Selection */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`w-[180px] justify-between ${useDateRange ? 'opacity-50' : ''}`}
                  disabled={useDateRange}
                >
                  <span className="truncate">
                    {weekFilter.length === 0 
                      ? (t('packages.allWeeks'))
                      : weekFilter.length === 1 
                        ? `${t('packages.weekLabel')} ${weekFilter[0]}`
                        : `${weekFilter.length} ${t('packages.weeksSelected')}`
                    }
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <div className="p-3 border-b">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('packages.selectWeeks')}</span>
                    {weekFilter.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs"
                        onClick={() => setWeekFilter([])}
                      >
                        <X className="h-3 w-3 mr-1" />
                        {t('packages.clearSelection')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto p-2">
                  <div className="grid grid-cols-4 gap-1">
                    {availableWeeks.map(week => {
                      const isSelected = weekFilter.includes(week.toString());
                      return (
                        <Button
                          key={week}
                          variant={isSelected ? "default" : "ghost"}
                          size="sm"
                          className={`h-8 text-xs ${isSelected ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setWeekFilter(weekFilter.filter(w => w !== week.toString()));
                            } else {
                              setWeekFilter([...weekFilter, week.toString()]);
                            }
                          }}
                        >
                          {week}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                {weekFilter.length > 0 && (
                  <div className="p-2 border-t bg-slate-50 dark:bg-slate-800">
                    <div className="flex flex-wrap gap-1">
                      {weekFilter.sort((a, b) => Number(a) - Number(b)).map(week => (
                        <Badge 
                          key={week} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-slate-200"
                          onClick={() => setWeekFilter(weekFilter.filter(w => w !== week))}
                        >
                          {t('packages.weekLabel')} {week}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Date Range Filter Toggle */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={useDateRange ? "default" : "outline"} 
                  className={`justify-between ${useDateRange ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {useDateRange && dateRangeStart && dateRangeEnd
                    ? `${dateRangeStart} - ${dateRangeEnd}`
                    : t('packages.dateRange')
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('packages.dateRangeFilter')}</span>
                    <Checkbox
                      checked={useDateRange}
                      onCheckedChange={(checked) => {
                        setUseDateRange(checked);
                        if (!checked) {
                          setDateRangeStart('');
                          setDateRangeEnd('');
                        }
                      }}
                    />
                  </div>
                  {useDateRange && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">{t('packages.startDate')}</label>
                        <Input
                          type="date"
                          value={dateRangeStart}
                          onChange={(e) => setDateRangeStart(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">{t('packages.endDate')}</label>
                        <Input
                          type="date"
                          value={dateRangeEnd}
                          onChange={(e) => setDateRangeEnd(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      {dateRangeStart && dateRangeEnd && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full text-xs"
                          onClick={() => {
                            setUseDateRange(false);
                            setDateRangeStart('');
                            setDateRangeEnd('');
                          }}
                        >
                          <X className="h-3 w-3 mr-1" />
                          {t('packages.clearSelection')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <Select value={globalCompanyFilter || "all"} onValueChange={(v) => setGlobalCompanyFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('packages.allCompanies')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('packages.allCompanies')}</SelectItem>
                {allCompanies.map(company => (
                  <SelectItem key={company} value={company}>{company}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear All Filters Button */}
            {(monthFilter.length > 0 || weekFilter.length > 0 || yearFilter || globalCompanyFilter || useDateRange) && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => { 
                  setMonthFilter([]);
                  setWeekFilter([]); 
                  setYearFilter(new Date().getFullYear().toString()); 
                  setGlobalCompanyFilter(''); 
                  setUseDateRange(false);
                  setDateRangeStart('');
                  setDateRangeEnd('');
                }}
              >
                <X className="h-4 w-4 mr-1" />
                {t('common.clearFilters')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {/* Toplam Fatura */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('packages.totalInvoices')}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {statsLoading ? '...' : formatNumber(statistics?.summary?.total_invoices || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toplam Tutar - Slightly wider */}
        <Card className="col-span-2 sm:col-span-1 md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('packages.totalAmount')}
                </p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 whitespace-nowrap">
                  {statsLoading ? '...' : formatCurrencyCompact(statistics?.summary?.total_amount || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Euro className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Çalışan Sayısı */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('packages.totalEmployees')}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {statsLoading ? '...' : formatNumber(statistics?.employees?.length || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bölge Sayısı */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('packages.totalRegions')}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {statsLoading ? '...' : formatNumber(statistics?.regions?.length || 0)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Toplam Adet */}
        <Card className="hidden md:block">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('packages.totalQuantity')}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {statsLoading ? '...' : formatNumber(statistics?.summary?.total_quantity || 0)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-cyan-100 flex items-center justify-center">
                <Boxes className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-2 px-2 scrollbar-hide">
          <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 h-auto p-1.5 bg-slate-100/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl gap-1">
            <TabsTrigger value="overview" className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-700 dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-blue-300 data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-700 data-[state=inactive]:hover:bg-white/50 whitespace-nowrap">
              <BarChart3 className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{t('packages.overview')}</span>
              <span className="sm:hidden text-xs">Genel</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-700 dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-blue-300 data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-700 data-[state=inactive]:hover:bg-white/50 whitespace-nowrap">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{t('packages.invoices')}</span>
              <span className="sm:hidden text-xs">Fatura</span>
            </TabsTrigger>
            <TabsTrigger value="employees" className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-700 dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-blue-300 data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-700 data-[state=inactive]:hover:bg-white/50 whitespace-nowrap">
              <Users className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{t('packages.employees')}</span>
              <span className="sm:hidden text-xs">Çalışan</span>
            </TabsTrigger>
            <TabsTrigger value="regions" className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-700 dark:data-[state=active]:bg-slate-700 dark:data-[state=active]:text-blue-300 data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-700 data-[state=inactive]:hover:bg-white/50 whitespace-nowrap">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{t('packages.regions')}</span>
              <span className="sm:hidden text-xs">Bölge</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Chart */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {t('packages.weeklyTrend')}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant={weeklyTrendMode === 'amount' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWeeklyTrendMode('amount')}
                    >
                      {t('packages.amount')}
                    </Button>
                    <Button
                      variant={weeklyTrendMode === 'count' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWeeklyTrendMode('count')}
                    >
                      {t('packages.packageCount')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {weeklyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={weeklyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        formatter={(value) => weeklyTrendMode === 'amount' ? formatCurrency(value) : `${value} ${t('packages.packageCount')}`}
                        labelStyle={{ color: '#334155' }}
                      />
                      <Bar 
                        dataKey={weeklyTrendMode === 'amount' ? 'amount' : 'count'} 
                        fill={weeklyTrendMode === 'amount' ? '#3b82f6' : '#10b981'} 
                        radius={[4, 4, 0, 0]} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Region Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('packages.regionDistribution')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {regionChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={regionChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {regionChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Yearly Comparison Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Yearly Amount/Package Comparison */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    {t('packages.yearlyComparison')}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant={yearlyChartMode === 'amount' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setYearlyChartMode('amount')}
                    >
                      {t('packages.amount')}
                    </Button>
                    <Button
                      variant={yearlyChartMode === 'count' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setYearlyChartMode('count')}
                    >
                      {t('packages.packageCount')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {comparisonLoading ? (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : yearlyComparison?.yearly?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={yearlyComparison.yearly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis 
                        fontSize={12} 
                        tickFormatter={(value) => yearlyChartMode === 'amount' ? `€${(value / 1000).toFixed(0)}K` : formatNumber(value)} 
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (yearlyChartMode === 'amount') return [formatCurrency(value), t('packages.totalAmountLabel')];
                          return [formatNumber(value), t('packages.totalPackageLabel')];
                        }}
                      />
                      <Bar 
                        dataKey={yearlyChartMode === 'amount' ? 'total_amount' : 'total_quantity'} 
                        fill={yearlyChartMode === 'amount' ? '#3b82f6' : '#8b5cf6'} 
                        radius={[4, 4, 0, 0]} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Yearly Ratio Chart - Amount per Package */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Boxes className="h-5 w-5 text-purple-600" />
                  {t('packages.amountPerPackage')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {comparisonLoading ? (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : yearlyComparison?.yearly?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={yearlyComparison.yearly.map(y => ({
                      ...y,
                      avg_per_package: y.total_quantity > 0 ? (y.total_amount / y.total_quantity) : 0,
                      change_percent: (() => {
                        const idx = yearlyComparison.yearly.findIndex(item => item.year === y.year);
                        if (idx === 0) return 0;
                        const prev = yearlyComparison.yearly[idx - 1];
                        const prevAvg = prev.total_quantity > 0 ? (prev.total_amount / prev.total_quantity) : 0;
                        const currAvg = y.total_quantity > 0 ? (y.total_amount / y.total_quantity) : 0;
                        if (prevAvg === 0) return 0;
                        return ((currAvg - prevAvg) / prevAvg * 100);
                      })()
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" fontSize={12} />
                      <YAxis yAxisId="left" fontSize={12} tickFormatter={(value) => `€${value.toFixed(0)}`} />
                      <YAxis yAxisId="right" orientation="right" fontSize={12} tickFormatter={(value) => `${value.toFixed(0)}%`} />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'avg_per_package') return [`€${value.toFixed(2)}`, t('packages.amountPerPackageLabel')];
                          if (name === 'change_percent') return [`${value.toFixed(1)}%`, t('packages.changePercent')];
                          return [value, name];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          if (value === 'avg_per_package') return t('packages.amountPerPackageLabel');
                          if (value === 'change_percent') return t('packages.changePercent');
                          return value;
                        }}
                      />
                      <Bar yAxisId="left" dataKey="avg_per_package" fill="#8b5cf6" name="avg_per_package" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="change_percent" stroke="#f59e0b" strokeWidth={3} name="change_percent" dot={{ fill: '#f59e0b', r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Weekly Comparison Chart - Last 3 Years */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-green-600" />
                  {t('packages.weeklyYearComparison')} 
                  <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                    ({t('packages.last4Years')})
                  </span>
                  {comparisonLocationFilter && (
                    <Badge variant="outline" className="ml-2 text-xs bg-purple-50 text-purple-700 border-purple-200">
                      📍 {comparisonLocationFilter}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Location Filter */}
                  {availableLocations.length > 0 && (
                    <Select
                      value={comparisonLocationFilter || "all"}
                      onValueChange={(val) => setComparisonLocationFilter(val === "all" ? "" : val)}
                    >
                      <SelectTrigger className="w-[160px] h-8 text-xs">
                        <SelectValue placeholder={t('packages.allLocations')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('packages.allLocations')}</SelectItem>
                        {availableLocations.map(loc => (
                          <SelectItem key={loc} value={loc}>
                            📍 {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* Tutar/Paket Toggle */}
                  <div className="flex gap-1">
                    <Button
                      variant={weeklyComparisonMode === 'amount' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWeeklyComparisonMode('amount')}
                    >
                      {t('packages.amount')}
                    </Button>
                    <Button
                      variant={weeklyComparisonMode === 'count' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setWeeklyComparisonMode('count')}
                    >
                      {t('packages.packageCount')}
                    </Button>
                  </div>
                  {/* Year Toggle */}
                  {yearlyComparison?.weekly_comparison?.length > 0 && (
                    <div className="flex gap-1 ml-2 pl-2 border-l">
                      {[2023, 2024, 2025, 2026].map(year => (
                        <Button
                          key={year}
                          variant={visibleYears[year] !== false ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setVisibleYears(prev => ({
                            ...prev,
                            [year]: prev[year] === false ? true : false
                          }))}
                          className="min-w-[50px]"
                          style={{
                            backgroundColor: visibleYears[year] !== false ? 
                              (year === 2023 ? '#f59e0b' : year === 2024 ? '#94a3b8' : year === 2025 ? '#3b82f6' : '#10b981') : undefined
                          }}
                        >
                          {year}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {comparisonLoading ? (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                </div>
              ) : yearlyComparison?.weekly_comparison?.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={yearlyComparison.weekly_comparison}>
                    <defs>
                      <linearGradient id="color2023" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="color2024" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#94a3b8" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="color2025" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="color2026" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="week" 
                      fontSize={11} 
                      tickFormatter={(week) => `H${week}`}
                    />
                    <YAxis 
                      fontSize={11} 
                      tickFormatter={(value) => weeklyComparisonMode === 'amount' 
                        ? `€${(value / 1000).toFixed(0)}K` 
                        : formatNumber(value)
                      } 
                    />
                    <Tooltip 
                      labelFormatter={(week) => `${t('packages.weekLabel')} ${week}`}
                      formatter={(value, name) => {
                        const year = name.replace(weeklyComparisonMode === 'amount' ? 'amount_' : 'quantity_', '');
                        return [
                          weeklyComparisonMode === 'amount' ? formatCurrency(value) : `${formatNumber(value)} paket`, 
                          `${year}`
                        ];
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const year = value.replace(weeklyComparisonMode === 'amount' ? 'amount_' : 'quantity_', '');
                        return `${year}`;
                      }}
                      onClick={(e) => {
                        const year = parseInt(e.dataKey.replace(weeklyComparisonMode === 'amount' ? 'amount_' : 'quantity_', ''));
                        setVisibleYears(prev => ({
                          ...prev,
                          [year]: prev[year] === false ? true : false
                        }));
                      }}
                    />
                    <Brush 
                      dataKey="week" 
                      height={28} 
                      stroke="#3b82f6"
                      tickFormatter={(week) => `H${week}`}
                      fill="#f1f5f9"
                    />
                    {visibleYears[2023] !== false && (
                      <Area 
                        type="monotone" 
                        dataKey={weeklyComparisonMode === 'amount' ? 'amount_2023' : 'quantity_2023'} 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#color2023)" 
                      />
                    )}
                    {visibleYears[2024] !== false && (
                      <Area 
                        type="monotone" 
                        dataKey={weeklyComparisonMode === 'amount' ? 'amount_2024' : 'quantity_2024'} 
                        stroke="#94a3b8" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#color2024)" 
                      />
                    )}
                    {visibleYears[2025] !== false && (
                      <Area 
                        type="monotone" 
                        dataKey={weeklyComparisonMode === 'amount' ? 'amount_2025' : 'quantity_2025'} 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#color2025)" 
                      />
                    )}
                    {visibleYears[2026] !== false && (
                      <Area 
                        type="monotone" 
                        dataKey={weeklyComparisonMode === 'amount' ? 'amount_2026' : 'quantity_2026'} 
                        stroke="#10b981" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#color2026)" 
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  {t('common.noData')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Employees Chart */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {topEmployeesMode === 'amount' 
                    ? (t('packages.topEmployees'))
                    : (t('packages.topEmployeesByPackage'))
                  }
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={topEmployeesMode === 'amount' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTopEmployeesMode('amount')}
                  >
                    {t('packages.amount')}
                  </Button>
                  <Button
                    variant={topEmployeesMode === 'count' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTopEmployeesMode('count')}
                  >
                    {t('packages.packageCount')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {employeeChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={employeeChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      fontSize={12} 
                      tickFormatter={(value) => topEmployeesMode === 'amount' 
                        ? `€${(value / 1000).toFixed(0)}K` 
                        : formatNumber(value)
                      }
                    />
                    <YAxis dataKey="name" type="category" fontSize={11} width={120} />
                    <Tooltip 
                      formatter={(value) => topEmployeesMode === 'amount' 
                        ? formatCurrency(value) 
                        : `${formatNumber(value)} paket`
                      } 
                    />
                    <Bar 
                      dataKey={topEmployeesMode === 'amount' ? 'earnings' : 'quantity'} 
                      fill={topEmployeesMode === 'amount' ? '#10b981' : '#8b5cf6'} 
                      radius={[0, 4, 4, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  {t('common.noData')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Driver-Location Performance Matrix */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-600" />
                  {t('packages.driverLocationMatrix')}
                </CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant={matrixMetric === 'quantity' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixMetric('quantity')}
                  >
                    {t('packages.packageCount')}
                  </Button>
                  <Button
                    variant={matrixMetric === 'amount' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixMetric('amount')}
                  >
                    {t('packages.amount')}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('packages.driverLocationMatrixDesc')}
                </p>
                {/* Time Period Filter */}
                <div className="flex gap-1">
                  <Button
                    variant={matrixTimePeriod === 'daily' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixTimePeriod('daily')}
                    className="text-xs h-7"
                  >
                    {t('packages.last7Days')}
                  </Button>
                  <Button
                    variant={matrixTimePeriod === 'weekly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixTimePeriod('weekly')}
                    className="text-xs h-7"
                  >
                    {t('packages.last4Weeks')}
                  </Button>
                  <Button
                    variant={matrixTimePeriod === 'monthly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixTimePeriod('monthly')}
                    className="text-xs h-7"
                  >
                    {t('packages.last3Months')}
                  </Button>
                  <Button
                    variant={matrixTimePeriod === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMatrixTimePeriod('all')}
                    className="text-xs h-7"
                  >
                    {t('packages.allTime')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {matrixLoading ? (
                <div className="h-[400px] flex items-center justify-center text-slate-400">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                </div>
              ) : driverLocationMatrix?.matrix?.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white dark:bg-slate-900 z-10 min-w-[150px]">
                          {t('packages.driver')}
                        </TableHead>
                        {driverLocationMatrix.locations?.slice(0, 10).map(loc => (
                          <TableHead key={loc} className="text-center min-w-[100px]">
                            <Badge variant="outline" className="text-xs">
                              {loc}
                            </Badge>
                          </TableHead>
                        ))}
                        <TableHead className="text-right min-w-[100px] font-bold bg-slate-50 dark:bg-slate-800">
                          {t('packages.total')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driverLocationMatrix.matrix.slice(0, 15).map((driver, idx) => (
                        <TableRow key={idx} className={idx < 3 ? 'bg-green-50/30 dark:bg-green-900/10' : ''}>
                          <TableCell className="sticky left-0 bg-white dark:bg-slate-900 font-medium">
                            <div className="flex items-center gap-2">
                              {idx < 3 && (
                                <Badge variant={idx === 0 ? 'default' : 'secondary'} className="text-xs w-5 h-5 p-0 flex items-center justify-center">
                                  {idx + 1}
                                </Badge>
                              )}
                              <span className="truncate max-w-[120px]">{driver.driver?.split(',')[0] || driver.driver}</span>
                            </div>
                          </TableCell>
                          {driverLocationMatrix.locations?.slice(0, 10).map(loc => {
                            const locData = driver.locations?.[loc];
                            const value = matrixMetric === 'quantity' 
                              ? locData?.quantity 
                              : locData?.amount;
                            const maxValue = matrixMetric === 'quantity'
                              ? Math.max(...driverLocationMatrix.matrix.map(d => d.totals?.total_quantity || 0))
                              : Math.max(...driverLocationMatrix.matrix.map(d => d.totals?.total_amount || 0));
                            const intensity = value ? Math.min(value / (maxValue * 0.3), 1) : 0;
                            
                            return (
                              <TableCell 
                                key={loc} 
                                className="text-center"
                                style={{
                                  backgroundColor: value 
                                    ? `rgba(59, 130, 246, ${intensity * 0.3})` 
                                    : 'transparent'
                                }}
                              >
                                {value ? (
                                  matrixMetric === 'quantity' 
                                    ? formatNumber(value)
                                    : formatCurrency(value)
                                ) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-bold bg-slate-50 dark:bg-slate-800">
                            {matrixMetric === 'quantity' 
                              ? formatNumber(driver.totals?.total_quantity || 0)
                              : formatCurrency(driver.totals?.total_amount || 0)
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Location Totals Row */}
                      <TableRow className="bg-slate-100 dark:bg-slate-700 font-bold">
                        <TableCell className="sticky left-0 bg-slate-100 dark:bg-slate-700">
                          {t('packages.locationTotal')}
                        </TableCell>
                        {driverLocationMatrix.locations?.slice(0, 10).map(loc => (
                          <TableCell key={loc} className="text-center">
                            {matrixMetric === 'quantity'
                              ? formatNumber(driverLocationMatrix.location_totals?.[loc]?.quantity || 0)
                              : formatCurrency(driverLocationMatrix.location_totals?.[loc]?.amount || 0)
                            }
                          </TableCell>
                        ))}
                        <TableCell className="text-right bg-slate-200 dark:bg-slate-600">
                          {matrixMetric === 'quantity'
                            ? formatNumber(driverLocationMatrix.matrix.reduce((sum, d) => sum + (d.totals?.total_quantity || 0), 0))
                            : formatCurrency(driverLocationMatrix.matrix.reduce((sum, d) => sum + (d.totals?.total_amount || 0), 0))
                          }
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  {driverLocationMatrix.driver_count > 15 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {t('packages.showingTopDrivers')} 15 / {driverLocationMatrix.driver_count} {t('packages.drivers')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400">
                  {t('common.noData')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seasonal Trend Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly/Quarterly Trends */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-amber-600" />
                    {t('packages.seasonalTrends')}
                  </CardTitle>
                  {seasonalComparisonMode === 'yearly' && (
                    <div className="flex gap-1">
                      <Button
                        variant={seasonalViewMode === 'monthly' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSeasonalViewMode('monthly')}
                      >
                        {t('packages.monthly')}
                      </Button>
                      <Button
                        variant={seasonalViewMode === 'quarterly' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSeasonalViewMode('quarterly')}
                      >
                        {t('packages.quarterly')}
                      </Button>
                    </div>
                  )}
                </div>
                {/* Comparison Mode Selector */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className="flex gap-1">
                    <Button
                      variant={seasonalComparisonMode === 'yearly' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeasonalComparisonMode('yearly')}
                      className="text-xs h-7"
                    >
                      {t('packages.yearlyComparison')}
                    </Button>
                    <Button
                      variant={seasonalComparisonMode === 'last6months' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeasonalComparisonMode('last6months')}
                      className="text-xs h-7"
                    >
                      {t('packages.last6MonthsVs')}
                    </Button>
                    <Button
                      variant={seasonalComparisonMode === 'last12months' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeasonalComparisonMode('last12months')}
                      className="text-xs h-7"
                    >
                      {t('packages.last12MonthsVs')}
                    </Button>
                  </div>
                </div>
                {seasonalComparisonMode === 'yearly' && (
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant={seasonalMetric === 'quantity' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeasonalMetric('quantity')}
                      className="text-xs h-7"
                    >
                      {t('packages.packageCount')}
                    </Button>
                    <Button
                      variant={seasonalMetric === 'amount' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeasonalMetric('amount')}
                      className="text-xs h-7"
                    >
                      {t('packages.amount')}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {seasonalLoading ? (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : seasonalComparisonMode !== 'yearly' && seasonalTrends?.period_comparison ? (
                  // Period comparison view (last 6 months vs previous, etc.)
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Current Period */}
                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <h4 className="font-medium text-sm text-green-700 dark:text-green-400 mb-2">
                          {seasonalTrends.period_comparison.current?.label || (seasonalComparisonMode === 'last6months' ? 'Son 6 Ay' : 'Son 12 Ay')}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.packageCount')}</span>
                            <span className="font-bold text-lg">{formatNumber(seasonalTrends.period_comparison.current?.total_quantity || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.amount')}</span>
                            <span className="font-bold text-lg text-green-600">{formatCurrency(seasonalTrends.period_comparison.current?.total_amount || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.invoices')}</span>
                            <span className="font-medium">{formatNumber(seasonalTrends.period_comparison.current?.invoice_count || 0)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Previous Period */}
                      <div className="p-4 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                        <h4 className="font-medium text-sm text-slate-600 dark:text-slate-400 mb-2">
                          {seasonalTrends.period_comparison.previous?.label || (seasonalComparisonMode === 'last6months' ? 'Önceki 6 Ay' : 'Önceki 12 Ay')}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.packageCount')}</span>
                            <span className="font-bold text-lg">{formatNumber(seasonalTrends.period_comparison.previous?.total_quantity || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.amount')}</span>
                            <span className="font-bold text-lg">{formatCurrency(seasonalTrends.period_comparison.previous?.total_amount || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">{t('packages.invoices')}</span>
                            <span className="font-medium">{formatNumber(seasonalTrends.period_comparison.previous?.invoice_count || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Growth Summary */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                      <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        {t('packages.periodGrowth')}
                      </h4>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('packages.amount')}:</span>
                          <Badge 
                            variant={seasonalTrends.period_comparison.growth?.amount_percent >= 0 ? 'default' : 'destructive'}
                            className={seasonalTrends.period_comparison.growth?.amount_percent >= 0 ? 'bg-green-100 text-green-700' : ''}
                          >
                            {seasonalTrends.period_comparison.growth?.amount_percent >= 0 ? '+' : ''}
                            {seasonalTrends.period_comparison.growth?.amount_percent?.toFixed(1)}%
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('packages.packageCount')}:</span>
                          <Badge 
                            variant={seasonalTrends.period_comparison.growth?.quantity_percent >= 0 ? 'default' : 'destructive'}
                            className={seasonalTrends.period_comparison.growth?.quantity_percent >= 0 ? 'bg-purple-100 text-purple-700' : ''}
                          >
                            {seasonalTrends.period_comparison.growth?.quantity_percent >= 0 ? '+' : ''}
                            {seasonalTrends.period_comparison.growth?.quantity_percent?.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : seasonalViewMode === 'monthly' && seasonalTrends?.monthly_comparison?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={seasonalTrends.monthly_comparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        fontSize={11}
                        tickFormatter={(m) => {
                          const shortMonths = {
                            tr: ['', 'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
                            en: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                            nl: ['', 'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
                            fr: ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
                          };
                          const lang = localStorage.getItem('oms-language') || 'nl';
                          return (shortMonths[lang] || shortMonths.nl)[m] || m;
                        }}
                      />
                      <YAxis 
                        fontSize={11}
                        tickFormatter={(value) => seasonalMetric === 'amount' 
                          ? `€${(value / 1000).toFixed(0)}K` 
                          : formatNumber(value)
                        }
                      />
                      <Tooltip 
                        labelFormatter={(m) => {
                          const months = {
                            tr: ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
                            en: ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                            nl: ['', 'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'],
                            fr: ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
                          };
                          const lang = localStorage.getItem('oms-language') || 'nl';
                          return (months[lang] || months.nl)[m] || m;
                        }}
                        formatter={(value, name) => {
                          const year = name.replace(seasonalMetric === 'amount' ? 'amount_' : 'quantity_', '');
                          return [
                            seasonalMetric === 'amount' ? formatCurrency(value) : `${formatNumber(value)} ${t('packages.packageCount')}`,
                            year
                          ];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const year = value.replace(seasonalMetric === 'amount' ? 'amount_' : 'quantity_', '');
                          return `${year}`;
                        }}
                      />
                      {seasonalTrends.insights?.years_analyzed?.map((year, idx) => (
                        <Bar 
                          key={year}
                          dataKey={seasonalMetric === 'amount' ? `amount_${year}` : `quantity_${year}`}
                          fill={YEAR_COLORS[idx % YEAR_COLORS.length]}
                          radius={[2, 2, 0, 0]}
                        />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : seasonalViewMode === 'quarterly' && seasonalTrends?.quarterly_comparison?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={seasonalTrends.quarterly_comparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="quarter_label" 
                        fontSize={11}
                        tickFormatter={(q, idx) => {
                          const item = seasonalTrends.quarterly_comparison[idx];
                          return item ? `${item.year} ${q}` : q;
                        }}
                      />
                      <YAxis 
                        fontSize={11}
                        tickFormatter={(value) => seasonalMetric === 'amount' 
                          ? `€${(value / 1000).toFixed(0)}K` 
                          : formatNumber(value)
                        }
                      />
                      <Tooltip 
                        formatter={(value) => [
                          seasonalMetric === 'amount' ? formatCurrency(value) : `${formatNumber(value)} ${t('packages.packageCount')}`,
                          seasonalMetric === 'amount' ? t('packages.amount') : t('packages.packageCount')
                        ]}
                      />
                      <Bar 
                        dataKey={seasonalMetric === 'amount' ? 'total_amount' : 'total_quantity'}
                        fill="#8b5cf6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Seasonal Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  {t('packages.seasonalInsights')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {seasonalLoading ? (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : seasonalTrends ? (
                  <>
                    {/* Year-over-Year Growth */}
                    {seasonalTrends.yoy_growth?.length > 0 && (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                        <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          {t('packages.yearOverYearGrowth')}
                        </h4>
                        <div className="space-y-2">
                          {seasonalTrends.yoy_growth.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <span className="text-sm">{item.previous_year} → {item.year}</span>
                              <div className="flex items-center gap-3">
                                <Badge 
                                  variant={item.growth_percent >= 0 ? 'default' : 'destructive'}
                                  className={item.growth_percent >= 0 ? 'bg-green-100 text-green-700' : ''}
                                >
                                  {item.growth_percent >= 0 ? '+' : ''}{item.growth_percent.toFixed(1)}% {t('packages.amount')}
                                </Badge>
                                <Badge 
                                  variant={item.quantity_growth_percent >= 0 ? 'default' : 'destructive'}
                                  className={item.quantity_growth_percent >= 0 ? 'bg-purple-100 text-purple-700' : ''}
                                >
                                  {item.quantity_growth_percent >= 0 ? '+' : ''}{item.quantity_growth_percent.toFixed(1)}% {t('packages.packageCount')}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Busiest Months */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Euro className="h-4 w-4 text-amber-600" />
                          {t('packages.busiestByAmount')}
                        </h4>
                        <div className="space-y-1">
                          {seasonalTrends.insights?.busiest_months_by_amount?.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="w-5 h-5 p-0 flex items-center justify-center text-xs">
                                  {idx + 1}
                                </Badge>
                                {translateMonth(m.month_name)}
                              </span>
                              <span className="font-medium text-amber-700">
                                {formatCurrencyCompact(m.avg_amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Package className="h-4 w-4 text-purple-600" />
                          {t('packages.busiestByPackage')}
                        </h4>
                        <div className="space-y-1">
                          {seasonalTrends.insights?.busiest_months_by_quantity?.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="w-5 h-5 p-0 flex items-center justify-center text-xs">
                                  {idx + 1}
                                </Badge>
                                {translateMonth(m.month_name)}
                              </span>
                              <span className="font-medium text-purple-700">
                                {formatNumber(Math.round(m.avg_quantity))}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Seasonal Pattern Radar Chart */}
                    {seasonalTrends.seasonal_pattern?.length > 0 && (
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h4 className="font-medium text-sm mb-3">
                          {t('packages.monthlyPattern')}
                        </h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <RadarChart data={seasonalTrends.seasonal_pattern.map(p => ({...p, month_name: translateMonth(p.month_name)}))}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="month_name" fontSize={10} />
                            <PolarRadiusAxis fontSize={9} />
                            <Radar 
                              name={t('packages.avgQuantity')} 
                              dataKey="avg_quantity" 
                              stroke="#8b5cf6" 
                              fill="#8b5cf6" 
                              fillOpacity={0.4} 
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    {t('common.noData')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader className="flex flex-col space-y-4 pb-4">
              <div className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle>{t('packages.invoiceList')}</CardTitle>
                  {selectedInvoices.size > 0 && (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      {selectedInvoices.size} {t('packages.selected')}
                    </Badge>
                  )}
                </div>
                {selectedInvoices.size > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setBulkDeleteDialog(true)}
                    className="flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('packages.deleteSelected')} ({selectedInvoices.size})
                  </Button>
                )}
              </div>
              
              {/* Search and Sort Row */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Invoice Number Search */}
                <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t('packages.searchInvoice')}
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Sort By */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px]">
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sırala" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice_date">{t('packages.invoiceDate')}</SelectItem>
                    <SelectItem value="invoice_number">{t('packages.invoiceNumber')}</SelectItem>
                    <SelectItem value="company_name">{t('packages.company')}</SelectItem>
                    <SelectItem value="total_amount">{t('packages.amount')}</SelectItem>
                    <SelectItem value="created_at">{t('packages.createdAt')}</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Sort Order Toggle */}
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  title={sortOrder === 'desc' ? 'Azalan' : 'Artan'}
                >
                  {sortOrder === 'desc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </Button>
                
                {/* Company Filter */}
                <Select value={companyFilter || "all"} onValueChange={(v) => setCompanyFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('packages.allCompanies')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('packages.allCompanies')}</SelectItem>
                    {uniqueCompanies.map(company => (
                      <SelectItem key={company} value={company}>{company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Location Filter */}
                <Select value={locationFilter || "all"} onValueChange={(v) => setLocationFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder={t('packages.allLocations')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('packages.allLocations')}</SelectItem>
                    {uniqueLocations.map(location => (
                      <SelectItem key={location} value={location}>{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Clear Filters */}
                {(companyFilter || locationFilter || invoiceSearch) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { 
                      setCompanyFilter(''); 
                      setLocationFilter(''); 
                      setInvoiceSearch('');
                    }}
                  >
                    {t('common.clearFilters')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  {t('common.noData')}
                </div>
              ) : filteredInvoices.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  {t('packages.noInvoiceFound')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {t('packages.showingInvoices')}: {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalInvoices)} / {t('packages.totalInvoices')}: {totalInvoices}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500 dark:text-slate-400">{t('packages.perPage')}:</span>
                      <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[80px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={filteredInvoices.length > 0 && selectedInvoices.size === filteredInvoices.length}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead>{t('packages.invoiceNumber')}</TableHead>
                        <TableHead>{t('packages.invoiceDate')}</TableHead>
                        <TableHead>{t('packages.weekNumber')}</TableHead>
                        <TableHead>{t('packages.company')}</TableHead>
                        <TableHead>{t('packages.location')}</TableHead>
                        <TableHead className="text-right">{t('packages.totalAmount')}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvoices.map((invoice) => (
                        <TableRow 
                          key={invoice.invoice_number}
                          className={selectedInvoices.has(invoice.invoice_number) ? 'bg-blue-50' : ''}
                        >
                          <TableCell>
                            <Checkbox 
                              checked={selectedInvoices.has(invoice.invoice_number)}
                              onCheckedChange={() => toggleInvoiceSelection(invoice.invoice_number)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{invoice.invoice_date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{t('packages.weekLabel')} {invoice.week_number}</Badge>
                          </TableCell>
                          <TableCell>{invoice.company_name}</TableCell>
                          <TableCell>{invoice.location}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(invoice.total_amount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewDetail(invoice)}
                                data-testid={`invoice-detail-${invoice.invoice_number}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <a
                                href={`https://pdf.hyk.be/pdf_viewer/${invoice.invoice_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`invoice-pdf-${invoice.invoice_number}`}
                              >
                                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50">
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </a>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteInvoice(invoice)}
                                data-testid={`invoice-delete-${invoice.invoice_number}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  {totalInvoices > pageSize && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {t('packages.page')} {currentPage} / {Math.ceil(totalInvoices / pageSize)}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          {'<<'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          {'<'}
                        </Button>
                        <span className="px-3 py-1 bg-slate-100 dark:bg-slate-700 rounded text-sm font-medium">
                          {currentPage}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalInvoices / pageSize), p + 1))}
                          disabled={currentPage >= Math.ceil(totalInvoices / pageSize)}
                        >
                          {'>'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.ceil(totalInvoices / pageSize))}
                          disabled={currentPage >= Math.ceil(totalInvoices / pageSize)}
                        >
                          {'>>'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle>{t('packages.employeeStats')}</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t('packages.searchEmployee')}
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-9 w-[250px]"
                  />
                </div>
                {employeeSearch && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEmployeeSearch('')}
                  >
                    {t('common.clearFilters')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : !statistics?.employees?.length ? (
                <div className="text-center py-8 text-slate-400">
                  {t('common.noData')}
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  &quot;{employeeSearch}&quot; {t('packages.noEmployeeFound')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                    {filteredEmployees.length} / {statistics.employees.length} {t('packages.employeesShown')}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('packages.employeeName')}</TableHead>
                        <TableHead className="text-right">{t('packages.totalQuantity')}</TableHead>
                        <TableHead className="text-right">{t('packages.routeDays') || 'Çalışılan Gün/Route'}</TableHead>
                        <TableHead className="text-right">{t('packages.workDays')}</TableHead>
                        <TableHead className="text-right">{t('packages.dailyAverage') || 'Günlük Ortalama'}</TableHead>
                        <TableHead className="text-right">{t('packages.difference') || 'Fark (165)'}</TableHead>
                        <TableHead className="text-right">{t('packages.totalEarnings')}</TableHead>
                        <TableHead>{t('packages.regions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEmployees.map((emp, idx) => {
                        const days = emp.unique_days_count || emp.work_days || 1;
                        const dailyAvg = Math.round((emp.total_quantity || 0) / days);
                        const fark = dailyAvg - HEDEF_ORTALAMA;
                        const isNegative = fark < 0;
                        const isPositive = fark > 0;
                        return (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{emp._id}</TableCell>
                          <TableCell className="text-right">{formatNumber(emp.total_quantity)}</TableCell>
                          <TableCell className="text-right">{emp.work_days}</TableCell>
                          <TableCell className="text-right">{days}</TableCell>
                          <TableCell className="text-right font-medium text-blue-600">
                            <span 
                              className="cursor-pointer hover:underline hover:text-blue-800 select-all"
                              title={t('packages.clickToCopy') || 'Kopyalamak için tıklayın'}
                              data-testid={`daily-avg-copy-${idx}`}
                              onClick={() => {
                                navigator.clipboard.writeText(dailyAvg.toString());
                                toast.success(`${dailyAvg} ${t('packages.copiedToClipboard') || 'kopyalandı'}`);
                              }}
                            >
                              {formatNumber(dailyAvg)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {isPositive ? (
                              <span 
                                className="font-semibold text-emerald-600 cursor-pointer hover:underline hover:text-emerald-800 px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/30"
                                data-testid={`fark-click-${idx}`}
                                onClick={() => {
                                  setBirimFiyat(0.5);
                                  setBirimFiyatInput('0,5');
                                  setBonusModal({ open: true, emp, fark, days });
                                }}
                              >
                                +{fark}
                              </span>
                            ) : fark === 0 ? (
                              <span className="text-slate-400">0</span>
                            ) : (
                              <span className="font-semibold text-red-600 px-2 py-0.5 rounded bg-red-50 dark:bg-red-950/30">{fark}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(emp.total_earnings)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {emp.regions?.map((region, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {region}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bonus Calculation Modal */}
        <Dialog open={bonusModal.open} onOpenChange={(open) => !open && setBonusModal({ open: false, emp: null, fark: 0, days: 0 })}>
          <DialogContent className="sm:max-w-[420px]" data-testid="bonus-modal">
            <DialogHeader>
              <DialogTitle className="text-lg">
                {bonusModal.emp?._id} - {t('packages.bonusCalculation') || 'Bonus Hesaplama'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                  <span className="text-slate-500 text-xs block">{t('packages.dailyAverage') || 'Günlük Ortalama'}</span>
                  <span className="font-bold text-lg">{HEDEF_ORTALAMA + bonusModal.fark}</span>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3">
                  <span className="text-slate-500 text-xs block">{t('packages.difference') || 'Fark'}</span>
                  <span className="font-bold text-lg text-emerald-600">+{bonusModal.fark}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                  {t('packages.unitPrice') || 'Birim Fiyat'} (€)
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={birimFiyatInput}
                  onChange={(e) => {
                    let val = e.target.value.replace('.', ',');
                    val = val.replace(/[^0-9,]/g, '');
                    setBirimFiyatInput(val);
                    const numVal = parseFloat(val.replace(',', '.')) || 0;
                    setBirimFiyat(numVal);
                  }}
                  className="w-full"
                  data-testid="bonus-birim-fiyat"
                  placeholder="1,00"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  {t('packages.calculation') || 'Hesaplama'}:
                </div>
                <div className="font-mono text-sm mb-1">
                  {birimFiyatInput || '0'} &times; ({bonusModal.days} {t('packages.dayShort') || 'gün'} &times; {bonusModal.fark}) = 
                </div>
                <div className="font-mono text-sm mb-3">
                  {birimFiyatInput || '0'} &times; {bonusModal.days * bonusModal.fark} = 
                </div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                  {t('packages.total') || 'Tutar'}: €{(birimFiyat * bonusModal.days * bonusModal.fark).toFixed(2).replace('.', ',')}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBonusModal({ open: false, emp: null, fark: 0, days: 0 })}>
                {t('common.close') || 'Kapat'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Regions Tab */}
        <TabsContent value="regions">
          <Card>
            <CardHeader>
              <CardTitle>{t('packages.regionStats')}</CardTitle>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : !statistics?.regions?.length ? (
                <div className="text-center py-8 text-slate-400">
                  {t('common.noData')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('packages.region')}</TableHead>
                        <TableHead className="text-right">{t('packages.employeeCount')}</TableHead>
                        <TableHead className="text-right">{t('packages.totalQuantity')}</TableHead>
                        <TableHead className="text-right">{t('packages.totalEarnings')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statistics.regions.map((reg, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              {reg.region || reg._id}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{reg.employee_count}</TableCell>
                          <TableCell className="text-right">{formatNumber(reg.total_quantity)}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(reg.total_earnings)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('packages.invoiceDetail')}: {selectedInvoice?.invoice_number}
              <a
                href={`https://pdf.hyk.be/pdf_viewer/${selectedInvoice?.invoice_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2"
                data-testid="invoice-detail-pdf-link"
              >
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-800 gap-1 h-7">
                  <FileText className="h-3.5 w-3.5" />
                  PDF
                </Button>
              </a>
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : invoiceDetail ? (
            <div className="flex flex-col gap-4 overflow-hidden">
              {/* Invoice Info */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.invoiceDate')}</p>
                  <p className="font-medium">{invoiceDetail.invoice?.invoice_date}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.weekNumber')}</p>
                  <p className="font-medium">{t('packages.weekLabel')} {invoiceDetail.invoice?.week_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.company')}</p>
                  <p className="font-medium">{invoiceDetail.invoice?.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.location')}</p>
                  <p className="font-medium">{invoiceDetail.invoice?.location}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.totalAmount')}</p>
                  <p className="font-medium text-green-600">{formatCurrency(invoiceDetail.invoice?.total_amount)}</p>
                </div>
              </div>

              {/* Employees Section with Filters */}
              {invoiceDetail.employees?.length > 0 && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Header with count and filters */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t('packages.employees')} 
                      <Badge variant="secondary">
                        {modalFilteredEmployees.length} / {invoiceDetail.employees.length}
                      </Badge>
                    </h4>
                    
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative">
                        <Users className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <Input
                          placeholder={t('packages.searchEmployee')}
                          value={modalEmployeeSearch}
                          onChange={(e) => setModalEmployeeSearch(e.target.value)}
                          className="pl-8 h-8 w-[180px] text-sm"
                        />
                      </div>
                      <Select value={modalRegionFilter || "all"} onValueChange={(v) => setModalRegionFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-8 w-[150px] text-sm">
                          <SelectValue placeholder={t('packages.allRegions')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('packages.allRegions')}</SelectItem>
                          {modalUniqueRegions.map(region => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={modalDateFilter || "all"} onValueChange={(v) => setModalDateFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-8 w-[130px] text-sm">
                          <SelectValue placeholder={t('packages.allDates')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('packages.allDates')}</SelectItem>
                          {modalUniqueDates.map(date => (
                            <SelectItem key={date} value={date}>{date}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(modalEmployeeSearch || modalRegionFilter || modalDateFilter) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8"
                          onClick={() => {
                            setModalEmployeeSearch('');
                            setModalRegionFilter('');
                            setModalDateFilter('');
                          }}
                        >
                          {t('common.clearFilters')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Scrollable Table */}
                  <div className="flex-1 overflow-auto border rounded-lg">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white dark:bg-slate-800 z-10">
                        <TableRow>
                          <TableHead className="w-[200px]">{t('packages.employeeName')}</TableHead>
                          <TableHead className="w-[100px]">{t('packages.date')}</TableHead>
                          <TableHead className="w-[120px]">{t('packages.region')}</TableHead>
                          <TableHead className="w-[80px] text-right">{t('packages.quantity')}</TableHead>
                          <TableHead className="w-[80px] text-right">{t('packages.unitPrice')}</TableHead>
                          <TableHead className="w-[80px] text-right">{t('packages.fixedCharge')}</TableHead>
                          <TableHead className="w-[100px] text-right">{t('packages.total')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {modalFilteredEmployees.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                              {t('common.noData')}
                            </TableCell>
                          </TableRow>
                        ) : (
                          modalFilteredEmployees.map((emp, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{emp.employee_name}</TableCell>
                              <TableCell>{emp.work_date}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">{emp.region}</Badge>
                              </TableCell>
                              <TableCell className="text-right">{formatNumber(emp.quantity)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(emp.unit_price)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(emp.fixed_charge)}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">{formatCurrency(emp.total)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totals Row */}
                  {modalFilteredEmployees.length > 0 && (
                    <div className="flex justify-end gap-6 mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="text-right">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.filteredQuantity')}</p>
                        <p className="font-bold text-lg">{formatNumber(modalFilteredTotals.quantity)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('packages.filteredTotal')}</p>
                        <p className="font-bold text-lg text-green-600">{formatCurrency(modalFilteredTotals.total)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Regions Summary - Compact */}
              {invoiceDetail.regions?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {t('packages.regionSummary')} ({invoiceDetail.regions.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {invoiceDetail.regions.map((reg, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary" 
                        className="px-3 py-1.5 cursor-pointer hover:bg-slate-200"
                        onClick={() => setModalRegionFilter(reg.region)}
                      >
                        {reg.region}: {formatCurrency(reg.total)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteInvoice} onOpenChange={() => setDeleteInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('packages.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteInvoice?.invoice_number}</strong> {t('packages.deleteConfirmMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteInvoice}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('packages.bulkDeleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>{selectedInvoices.size}</strong> {t('packages.bulkDeleteConfirmMessage')}
              </p>
              <p className="text-red-600 font-medium">
                {t('packages.bulkDeleteWarning')}
              </p>
              <div className="mt-3 max-h-32 overflow-y-auto bg-slate-50 dark:bg-slate-800 rounded p-2">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('packages.selectedInvoices')}:</p>
                <div className="flex flex-wrap gap-1">
                  {Array.from(selectedInvoices).map(invNo => (
                    <Badge key={invNo} variant="secondary" className="text-xs">
                      {invNo}
                    </Badge>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('packages.deleteAll')} ({selectedInvoices.size})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
